// Route Service - Manages community-drawn facility routes
// Handles saving, loading, and voting on caliche road routes

export interface RouteWaypoint {
  latitude: number;
  longitude: number;
  label?: string;
  type?: 'marker' | 'turn' | 'landmark' | 'warning' | 'gate';
}

export interface RouteData {
  encodedPolyline?: string;
  polyline?: Array<{ lat: number; lng: number }>;
  startLat?: number;
  startLng?: number;
  endLat?: number;
  endLng?: number;
}

export interface FacilityRoute {
  id: string;
  facilityId: string;
  facilityType: 'well' | 'swd' | 'rig' | 'other';
  userId: string;
  userName: string;
  routeData: RouteData;
  description?: string;
  startDescription?: string;
  upvotes: number;
  downvotes: number;
  score: number;
  waypoints?: RouteWaypoint[];
  waypointCount: number;
  userVote?: number; // 1, -1, or 0
  createdAt: string;
  updatedAt: string;
}

export interface RoutesResponse {
  success: boolean;
  routes?: FacilityRoute[];
  error?: string;
}

const API_BASE = '/api/routes';

export const RouteService = {
  /**
   * Get all routes for a facility
   */
  async getRoutes(facilityId: string, facilityType: string = 'well', userId?: string): Promise<RoutesResponse> {
    try {
      const params = new URLSearchParams({ facilityId, facilityType });
      if (userId) params.append('userId', userId);
      
      const response = await fetch(`${API_BASE}?${params}`);
      const data = await response.json();
      
      if (data.success) {
        // Transform snake_case to camelCase
        const routes = (data.routes || []).map((r: Record<string, unknown>) => ({
          id: r.id,
          facilityId: r.facility_id,
          facilityType: r.facility_type,
          userId: r.user_id,
          userName: r.user_name,
          routeData: r.route_data ? JSON.parse(r.route_data as string) : {},
          description: r.description,
          startDescription: r.start_description,
          upvotes: r.upvotes || 0,
          downvotes: r.downvotes || 0,
          score: r.score || 0,
          waypoints: r.waypoints || [],
          waypointCount: r.waypoint_count || 0,
          userVote: r.user_vote,
          createdAt: r.created_at,
          updatedAt: r.updated_at
        }));
        return { success: true, routes };
      }
      console.error('Failed to fetch routes:', data.error);
      return { success: false, error: data.error || 'Failed to fetch routes' };
    } catch (error) {
      console.error('Error fetching routes:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Submit a new route
   */
  async submitRoute(
    facilityId: string,
    facilityType: string,
    userId: string,
    userName: string,
    routeData: RouteData,
    description?: string,
    startDescription?: string,
    waypoints?: RouteWaypoint[]
  ): Promise<{ success: boolean; routeId?: string; error?: string }> {
    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          facilityId,
          facilityType,
          userId,
          userName,
          routeData,
          description,
          startDescription,
          waypoints
        })
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error submitting route:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Vote on a route (upvote/downvote)
   */
  async vote(routeId: string, userId: string, vote: 1 | -1 | 0): Promise<{
    success: boolean;
    upvotes?: number;
    downvotes?: number;
    score?: number;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ routeId, userId, vote })
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error voting:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Delete a route (only own routes)
   */
  async deleteRoute(routeId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const params = new URLSearchParams({ routeId, userId });
      const response = await fetch(`${API_BASE}?${params}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting route:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Convert Google Maps polyline to our format
   */
  polylineToRouteData(polyline: google.maps.Polyline): RouteData {
    const path = polyline.getPath();
    const points: Array<{ lat: number; lng: number }> = [];
    
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      points.push({ lat: point.lat(), lng: point.lng() });
    }
    
    const startPoint = points[0];
    const endPoint = points[points.length - 1];
    
    return {
      polyline: points,
      startLat: startPoint?.lat,
      startLng: startPoint?.lng,
      endLat: endPoint?.lat,
      endLng: endPoint?.lng
    };
  },

  /**
   * Draw a route on Google Maps
   */
  drawRouteOnMap(map: google.maps.Map, routeData: RouteData, options?: {
    strokeColor?: string;
    strokeWeight?: number;
    strokeOpacity?: number;
    clickable?: boolean;
  }): google.maps.Polyline | null {
    if (!routeData.polyline || routeData.polyline.length === 0) {
      return null;
    }
    
    const polyline = new google.maps.Polyline({
      path: routeData.polyline,
      strokeColor: options?.strokeColor || '#00f0ff',
      strokeWeight: options?.strokeWeight || 4,
      strokeOpacity: options?.strokeOpacity || 0.8,
      clickable: options?.clickable ?? true,
      map
    });
    
    return polyline;
  },

  /**
   * Draw waypoints on map
   */
  drawWaypointsOnMap(map: google.maps.Map, waypoints: RouteWaypoint[]): google.maps.Marker[] {
    const waypointIcons: Record<string, string> = {
      marker: '📍',
      turn: '↪️',
      landmark: '🏭',
      warning: '⚠️',
      gate: '🚧'
    };
    
    return waypoints.map((wp, index) => {
      return new google.maps.Marker({
        position: { lat: wp.latitude, lng: wp.longitude },
        map,
        title: wp.label || `Waypoint ${index + 1}`,
        label: {
          text: waypointIcons[wp.type || 'marker'],
          fontSize: '20px'
        }
      });
    });
  }
};

export default RouteService;
